/**
 * SE2 · Free-shipping quick add, the client half (2026-09-29, spec .design/specs/quiz-kit-2026-09-29.md G.4,
 * owner decision OD6).
 *
 * layout/theme.liquid loads this module on every page while Settings > SE2 · Free shipping popup is on, next to the
 * config island #se2-ship-gap-config (threshold and window in cents, currencies, country, money format, routes).
 * It never decides on its own: after a trigger it runs a cheap pre-check against /cart.js, then asks the server
 * half, sections/se2-ship-gap.liquid, through Section Rendering; only a data-show="true" answer carries a dialog.
 *
 * Triggers: any shopify:cart:lines-update whose promise settles as a successful 'add' (from any page, the PDP, the
 * kit, quick add), every open of the cart drawer (theme-drawer:open on #cart-drawer), a drawer the theme restored
 * open on load (no open event is fired for it), and se2:ship-gap:check. A trigger is never dropped: it waits in
 * wantCheck while cart updates are still in flight (product-form dispatches its event before the add lands, and the
 * drawer can open inside that dispatch), then runs 300ms after the last one settled.
 *
 * Once per session: the state in sessionStorage 'se2-ship-gap:v1' (shown | added | dismissed) means "never again";
 * when storage throws, an in-memory flag does the same for this page load.
 *
 * Layering and focus: the popup is a modal <dialog> appended to document.body, never inside #cart-drawer or
 * .page-wrapper. Below 990 the cart drawer is itself a modal dialog and the last showModal() wins. At 990 and up the
 * drawer is non-modal with trapFocus() from @theme/focus, so that trap is lifted while the popup is open and put
 * back when it closes. When the drawer changes mode under an open popup (a resize across 990), the drawer re-opens
 * itself with showModal() on top, so the popup restacks itself in the next frame.
 *
 * Esc closes only the popup: the dialog's keydown handler calls preventDefault(), which the drawer's document-level
 * Escape handler honours. The popup's own add is dispatched as a CartLinesUpdateEvent on the dialog, so the drawer
 * morphs, the header count updates, and a closed drawer opens once the popup has closed (Horizon's cart drawer waits
 * for that dialog's close event); this module ignores its own event.
 */

const STORE_KEY = 'se2-ship-gap:v1';
const DRAWER_SETTLE_MS = 450;
const NO_DRAWER_SETTLE_MS = 250;
const SETTLE_FLOOR_MS = 300;
const PENDING_TIMEOUT_MS = 15000;

const config = readConfig();

/** @type {string | null} */
let memoryState = null;
let pending = 0;
/** @type {'add' | 'drawer' | 'manual' | null} */
let wantCheck = null;
let inFlight = false;
let lastSettle = 0;
let drawerOpenedAt = 0;
let drawerOpenedHere = false;
let requeues = 0;
/** @type {number | undefined} */
let pumpTimer;

/**
 * @type {null | {
 *   node: HTMLDialogElement, data: Record<string, any>, trigger: string, previousFocus: Element | null,
 *   drawerTrapped: boolean, prevInDrawer: boolean, mq: MediaQueryList, onMq: () => void,
 *   restacking: boolean, closing: boolean, busy: boolean, autoClose?: number
 * }}
 */
let current = null;

/** @type {Promise<{ events: any, focus: any, money: any }> | null} */
let modulesPromise = null;

if (config && !document.documentElement.hasAttribute('data-se2-ship-gap-ready')) {
  document.documentElement.setAttribute('data-se2-ship-gap-ready', '');
  boot();
}

function readConfig() {
  const el = document.getElementById('se2-ship-gap-config');
  if (!el) return null;
  try {
    const parsed = JSON.parse(el.textContent || '{}');
    if (!parsed || !(Number(parsed.thresholdCents) > 0) || !(Number(parsed.windowCents) > 0)) return null;
    return parsed;
  } catch (error) {
    return null;
  }
}

function boot() {
  document.addEventListener('shopify:cart:lines-update', onLinesUpdate);

  document.addEventListener('theme-drawer:open', (event) => {
    if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
    drawerOpenedAt = performance.now();
    drawerOpenedHere = true;
    wantCheck ??= 'drawer';
    requeues = 0;
    // Deferred: the drawer can open inside a lines-update dispatch (product-form dispatches before it fetches), and
    // this module's own lines-update listener must count that update as pending before the pump looks.
    setTimeout(pump, 0);
  });

  document.addEventListener('se2:ship-gap:check', () => {
    wantCheck = 'manual';
    requeues = 0;
    pump();
  });

  // The theme's session restore opens the drawer on wide viewports without firing theme-drawer:open.
  if (document.querySelector('theme-drawer#cart-drawer[open]')) {
    wantCheck ??= 'drawer';
    pump();
  }
}

/** @param {Event & { action?: string, promise?: Promise<any> }} event */
function onLinesUpdate(event) {
  const target = event.target;
  if (target instanceof Element && target.closest('dialog[data-se2-ship-gap]')) return;

  pending += 1;
  const action = event.action;
  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true;
    pending = Math.max(0, pending - 1);
    lastSettle = performance.now();
    pump();
  };
  // A promise that never settles must not hold every later trigger back.
  const guard = setTimeout(settle, PENDING_TIMEOUT_MS);

  Promise.resolve(event.promise ?? {})
    .then((result) => {
      if (action === 'add' && !result?.detail?.didError) {
        wantCheck ??= 'add';
        requeues = 0;
      }
    })
    .catch(() => {})
    .finally(() => {
      clearTimeout(guard);
      settle();
    });
}

function pump() {
  if (seen()) {
    wantCheck = null;
    return;
  }
  if (!wantCheck || pending > 0 || inFlight) return;

  const wait = SETTLE_FLOOR_MS - (performance.now() - lastSettle);
  if (wait > 0) {
    clearTimeout(pumpTimer);
    pumpTimer = window.setTimeout(pump, wait);
    return;
  }

  const trigger = wantCheck;
  wantCheck = null;
  inFlight = true;
  evaluate(trigger)
    .catch((error) => console.warn('[se2-ship-gap]', error))
    .finally(() => {
      inFlight = false;
      pump();
    });
}

/**
 * @param {string} trigger
 * @returns {Promise<void>}
 */
async function evaluate(trigger) {
  if (seen()) return;

  // 1. Another modal is up (the quiz, a kit sheet, quick add): wait for it to close, then start again.
  const other = otherModal();
  if (other) {
    await waitForClose(other);
    return evaluate(trigger);
  }

  // 2. The cheap pre-check, mirroring the server's reasons, so most carts never cost a section request.
  const cart = await getCart();
  if (!precheck(cart)) return;

  // 3. The server decides.
  const root = await getSection();
  if (!root || root.getAttribute('data-show') !== 'true') return;
  const dialog = root.querySelector('dialog[data-se2-ship-gap]');
  if (!(dialog instanceof HTMLDialogElement)) return;
  loadModules();

  // 4. Let the drawer finish opening before the popup lands on it; warm the product photo meanwhile, so the sheet
  // does not rise with an empty frame (a parsed document never fetches its images).
  await Promise.all([settleDrawer(), preloadImage(dialog.querySelector('img'), 900)]);
  if (seen()) return;
  if (otherModal()) return requeue(trigger);

  // 5. The cart must still be the one the server judged.
  const again = await getCart();
  if (!again || again.token !== cart.token || again.item_count !== cart.item_count || again.total_price !== cart.total_price) {
    return requeue(trigger);
  }

  await open(dialog, trigger);
}

/** @param {string} trigger */
function requeue(trigger) {
  if (requeues >= 3) return;
  requeues += 1;
  wantCheck ??= /** @type {any} */ (trigger);
}

/**
 * A cheap pre-filter only, so a cart that clearly cannot qualify costs no section request. The rule itself (threshold,
 * plan lines) is written once, in snippets/se2-cart-shipping-progress.liquid (surface 'state', V-cart-13), and the
 * section that answers applies it; this mirror may only ever be looser than that, never stricter.
 * @param {any} cart
 */
function precheck(cart) {
  if (!cart || !(Number(cart.item_count) > 0)) return false;
  if ((cart.items || []).some((item) => item && item.selling_plan_allocation)) return false;
  if (cart.currency !== config.shopCurrency) return false;
  if (config.country !== 'US') return false;
  const remaining = Number(config.thresholdCents) - Number(cart.total_price);
  return remaining > 0 && remaining <= Number(config.windowCents);
}

/** @param {HTMLDialogElement} source */
async function open(source, trigger) {
  const { focus } = await loadModules();
  if (seen() || current) return;

  writeState('shown');
  for (const old of document.querySelectorAll('dialog[data-se2-ship-gap]')) old.remove();
  const node = /** @type {HTMLDialogElement} */ (document.importNode(source, true));
  document.body.append(node);

  const data = {
    remainingCents: Number(node.dataset.remainingCents) || 0,
    neededCents: Number(node.dataset.neededCents) || 0,
    thresholdCents: Number(node.dataset.thresholdCents) || Number(config.thresholdCents),
    variantId: Number(node.dataset.variantId) || 0,
    productId: Number(node.dataset.productId) || 0,
    priceCents: Number(node.dataset.priceCents) || 0,
    source: node.dataset.source || '',
    title: node.dataset.title || '',
  };

  const panel = drawerPanel();
  const previousFocus = document.activeElement;
  const panelOpen = Boolean(panel && panel.open);
  const prevInDrawer = Boolean(panelOpen && previousFocus && panel.contains(previousFocus));
  // 7. At 990 and up the drawer is non-modal and traps focus; lift the trap while the popup is up.
  const drawerTrapped = Boolean(panelOpen && !isModal(panel) && (prevInDrawer || drawerOpenedHere));
  if (drawerTrapped) focus?.removeTrapFocus?.();

  const mq = window.matchMedia('(max-width: 989px)');
  const state = {
    node,
    data,
    trigger,
    previousFocus,
    drawerTrapped,
    prevInDrawer,
    mq,
    onMq: () => restack(),
    restacking: false,
    closing: false,
    busy: false,
  };
  current = state;
  wire(node);

  // 8. Modal, over the page and over the drawer; focus on the title so the gap is what is announced.
  node.showModal();
  focusTitle(node);
  mq.addEventListener('change', state.onMq);

  // 9.
  emit('se2:ship-gap:open', {
    remainingCents: data.remainingCents,
    variantId: data.variantId,
    productId: data.productId,
    priceCents: data.priceCents,
    source: data.source,
    trigger,
  });
  publish('se2_ship_gap_show', { remainingCents: data.remainingCents, source: data.source });
}

/** Puts the popup back on top after the drawer re-opened itself in its other mode across 990. */
function restack() {
  const state = current;
  if (!state || state.closing) return;
  state.restacking = true;
  requestAnimationFrame(() => {
    if (current !== state || state.closing) return;
    const { node } = state;
    const active = document.activeElement;
    node.classList.add('is-static');
    if (node.open) node.close();
    node.showModal();
    // The drawer re-installs its trap when it turns non-modal (and drops it when it turns modal).
    const panel = drawerPanel();
    if (panel && panel.open && !isModal(panel)) {
      loadModules().then(({ focus }) => focus?.removeTrapFocus?.());
      state.drawerTrapped = true;
    } else {
      state.drawerTrapped = false;
    }
    if (active instanceof HTMLElement && node.contains(active)) active.focus({ preventScroll: true });
    else focusTitle(node);
    state.restacking = false;
    requestAnimationFrame(() => node.classList.remove('is-static'));
  });
}

/** @param {HTMLDialogElement} node */
function wire(node) {
  node.querySelector('[data-testid="se2-ship-gap-add"]')?.addEventListener('click', onAdd);
  node.querySelector('[data-testid="se2-ship-gap-dismiss"]')?.addEventListener('click', () => closePopup('dismiss'));
  node.querySelector('[data-testid="se2-ship-gap-close"]')?.addEventListener('click', () => closePopup('close'));

  node.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.isComposing) return;
    // Handled here, once: the drawer's document-level Escape handler returns on defaultPrevented.
    event.preventDefault();
    closePopup('escape');
  });
  node.addEventListener('cancel', (event) => {
    event.preventDefault();
    closePopup('escape');
  });
  node.addEventListener('click', (event) => {
    if (event.target !== node) return;
    const panel = node.querySelector('.se2-ship-gap__panel');
    const rect = (panel || node).getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) closePopup('backdrop');
  });
  // Closed by something else (a form, the browser): treat it as a close, once.
  node.addEventListener('close', () => {
    const state = current;
    if (!state || state.node !== node || state.closing || state.restacking || node.open) return;
    closePopup('close');
  });
}

async function onAdd() {
  const state = current;
  if (!state || state.busy || state.closing) return;
  state.busy = true;
  const { node, data } = state;
  const button = /** @type {HTMLButtonElement | null} */ (node.querySelector('[data-testid="se2-ship-gap-add"]'));
  const errorEl = /** @type {HTMLElement | null} */ (node.querySelector('[data-testid="se2-ship-gap-error"]'));
  const statusEl = /** @type {HTMLElement | null} */ (node.querySelector('[data-testid="se2-ship-gap-status"]'));
  const label = button ? button.textContent : '';

  if (errorEl) {
    errorEl.hidden = true;
    errorEl.textContent = '';
  }
  if (button) {
    button.textContent = node.dataset.addingText || 'Adding…';
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
  }

  const sections = [
    ...new Set(
      Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
        .filter(Boolean)
    ),
  ];

  /** @type {any} */
  let addJson = null;
  let ok = false;
  let description = '';
  try {
    const response = await fetch(jsUrl(config.cartAdd), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: data.variantId, quantity: 1 }],
        ...(sections.length ? { sections } : {}),
        sections_url: window.location.pathname,
      }),
    });
    const text = await response.text();
    try {
      addJson = JSON.parse(text);
    } catch (error) {
      addJson = null;
    }
    ok = response.ok && Boolean(addJson) && typeof addJson === 'object' && !addJson.status;
    if (!ok) description = String(addJson?.description || addJson?.message || '').trim();
  } catch (error) {
    ok = false;
  }

  if (current !== state) return;

  if (!ok) {
    if (errorEl) {
      errorEl.textContent = [node.dataset.errorText || `${data.title} could not be added.`, description]
        .filter(Boolean)
        .join(' ');
      errorEl.hidden = false;
    }
    if (button) {
      button.textContent = label;
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }
    state.busy = false;
    return;
  }

  /** @type {any} */
  let cartJson = null;
  try {
    cartJson = await getCart();
  } catch (error) {
    cartJson = null;
  }

  const threshold = data.thresholdCents;
  const total = cartJson ? Number(cartJson.total_price) : NaN;
  const reached = Number.isFinite(total) ? total >= threshold : data.priceCents >= data.neededCents;
  const remainingAfter = Number.isFinite(total) ? Math.max(0, threshold - total) : 0;

  // The theme's own contract: the drawer morphs from detail.sections, the header count updates, and a closed
  // drawer opens once this dialog has closed.
  try {
    const { events } = await loadModules();
    const CartLinesUpdateEvent = events?.CartLinesUpdateEvent;
    if (CartLinesUpdateEvent) {
      const deferred = CartLinesUpdateEvent.createPromise();
      node.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(data.variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: cartJson ? CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson) : null,
        detail: {
          sections: addJson.sections,
          items: cartJson ? cartJson.items : undefined,
          itemCount: cartJson ? cartJson.item_count : undefined,
          source: 'se2-ship-gap',
          didError: false,
        },
      });
    }
  } catch (error) {
    console.warn('[se2-ship-gap]', error);
  }

  let message = node.dataset.successText || '';
  if (!reached) {
    let amount = '';
    try {
      const { money } = await loadModules();
      amount = money?.formatMoney
        ? money.formatMoney(remainingAfter, config.moneyFormat || '${{amount}}', (cartJson && cartJson.currency) || config.currency)
        : '';
    } catch (error) {
      amount = '';
    }
    message = (node.dataset.partialText || '').replace('[amount]', amount);
  }

  node.dataset.state = 'added';
  node.dataset.reached = reached ? 'true' : 'false';
  // The add button and "No thanks" hide in this state; keep focus inside the dialog, on the close button. Focus
  // moves first and the status is written on the next frame: a polite update queued just before a focus change is
  // usually dropped by VoiceOver, and this one is the whole point ("Your order ships free in the U.S.").
  /** @type {HTMLElement | null} */ (node.querySelector('[data-testid="se2-ship-gap-close"]'))?.focus({ preventScroll: true });
  if (statusEl) {
    requestAnimationFrame(() => {
      statusEl.textContent = message;
    });
  }
  if (button) {
    button.removeAttribute('aria-busy');
    button.removeAttribute('aria-disabled');
  }

  writeState('added');
  emit('se2:ship-gap:added', { variantId: data.variantId, remainingCents: remainingAfter, reached });
  publish('se2_ship_gap_add', { variantId: data.variantId, reached });

  const delay = reached ? (reducedMotion() ? 1200 : 1600) : 2400;
  state.autoClose = window.setTimeout(() => closePopup('added'), delay);
}

/** @param {'added' | 'dismiss' | 'escape' | 'backdrop' | 'close'} reason */
async function closePopup(reason) {
  const state = current;
  if (!state || state.closing) return;
  state.closing = true;
  clearTimeout(state.autoClose);
  state.mq.removeEventListener('change', state.onMq);
  if (reason !== 'added' && readState() !== 'added') writeState('dismissed');

  const { node } = state;
  const { focus } = await loadModules().catch(() => ({ focus: null }));

  if (node.open) {
    node.classList.add('is-closing');
    await animationDone(node, reducedMotion() ? 40 : 260);
    // Removed only from its own close event: Horizon's cart drawer waits for that same event before it opens
    // after an add made from inside a modal dialog.
    node.addEventListener('close', () => setTimeout(() => node.remove(), 0), { once: true });
    node.close();
  } else {
    setTimeout(() => node.remove(), 0);
  }

  const panel = drawerPanel();
  const panelOpen = Boolean(panel && panel.open);
  if (state.drawerTrapped && panelOpen && !isModal(panel)) focus?.trapFocus?.(panel);
  const previous = state.previousFocus;
  if (previous instanceof HTMLElement && previous.isConnected && isVisible(previous)) {
    previous.focus({ preventScroll: true });
  } else if (panelOpen && (state.drawerTrapped || state.prevInDrawer || isModal(panel))) {
    /** @type {HTMLElement | null} */ (
      document.querySelector('#cart-drawer .se2-cart-drawer__close, #cart-drawer .theme-drawer__close-button')
    )?.focus({ preventScroll: true });
  }

  current = null;
  emit('se2:ship-gap:close', { reason });
  if (reason !== 'added') publish('se2_ship_gap_dismiss', { reason });
}

/* ---------- helpers ---------- */

function loadModules() {
  if (!modulesPromise) {
    modulesPromise = Promise.all([
      import('@shopify/events').catch(() => null),
      import('@theme/focus').catch(() => null),
      import('@theme/money-formatting').catch(() => null),
    ]).then(([events, focus, money]) => ({ events, focus, money }));
  }
  return modulesPromise;
}

/** @param {string} url */
function jsUrl(url) {
  return /\.js$/.test(url) ? url : `${url}.js`;
}

async function getCart() {
  const response = await fetch(jsUrl(config.cart || '/cart'), {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`cart ${response.status}`);
  return response.json();
}

async function getSection() {
  const section = config.section || 'se2-ship-gap';
  const response = await fetch(`${config.root || '/'}?sections=${encodeURIComponent(section)}`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return null;
  const json = await response.json();
  const html = json && json[section];
  if (typeof html !== 'string') return null;
  return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-se2-ship-gap-section]');
}

function settleDrawer() {
  const drawer = document.getElementById('cart-drawer');
  let wait = NO_DRAWER_SETTLE_MS;
  if (drawer && drawer.hasAttribute('open')) {
    wait = drawerOpenedAt ? Math.max(0, DRAWER_SETTLE_MS - (performance.now() - drawerOpenedAt)) : 0;
  }
  return new Promise((resolve) => setTimeout(resolve, wait));
}

/**
 * Resolves once the image has decoded, or after the cap, whichever comes first; never rejects.
 * @param {Element | null} img
 * @param {number} cap
 */
function preloadImage(img, cap) {
  const src = img instanceof HTMLImageElement ? img.getAttribute('src') : null;
  if (!src) return Promise.resolve();
  return new Promise((resolve) => {
    const probe = new Image();
    const timer = setTimeout(resolve, cap);
    const done = () => {
      clearTimeout(timer);
      resolve(undefined);
    };
    probe.onload = done;
    probe.onerror = done;
    probe.src = new URL(src, window.location.href).href;
  });
}

/** @returns {HTMLDialogElement | null} */
function drawerPanel() {
  const panel = document.querySelector('#cart-drawer dialog');
  return panel instanceof HTMLDialogElement ? panel : null;
}

/** @param {Element | null} element */
function isModal(element) {
  if (!element) return false;
  try {
    return element.matches(':modal');
  } catch (error) {
    return false;
  }
}

/** @returns {HTMLDialogElement | null} */
function otherModal() {
  for (const dialog of document.querySelectorAll('dialog[open]')) {
    if (!(dialog instanceof HTMLDialogElement)) continue;
    if (dialog.matches('[data-se2-ship-gap]') || dialog.closest('#cart-drawer')) continue;
    if (isModal(dialog)) return dialog;
  }
  return null;
}

/** @param {HTMLDialogElement} dialog */
function waitForClose(dialog) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearInterval(poll);
      dialog.removeEventListener('close', finish);
      // Let the closing dialog restore its focus first.
      setTimeout(resolve, 50);
    };
    dialog.addEventListener('close', finish, { once: true });
    // A dialog removed while open never fires close.
    const poll = setInterval(() => {
      if (!dialog.isConnected || !dialog.open) finish();
    }, 500);
  });
}

/**
 * @param {HTMLElement} node
 * @param {number} timeout
 */
function animationDone(node, timeout) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      node.removeEventListener('animationend', onEnd);
      resolve(undefined);
    };
    /** @param {AnimationEvent} event */
    const onEnd = (event) => {
      if (event.target === node) finish();
    };
    node.addEventListener('animationend', onEnd);
    setTimeout(finish, timeout);
  });
}

/** @param {HTMLDialogElement} node */
function focusTitle(node) {
  /** @type {HTMLElement | null} */ (node.querySelector('#se2-ship-gap-title'))?.focus({ preventScroll: true });
}

/** @param {HTMLElement} element */
function isVisible(element) {
  if (typeof element.checkVisibility === 'function') return element.checkVisibility();
  return element.getClientRects().length > 0;
}

function reducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (error) {
    return false;
  }
}

function seen() {
  if (memoryState) return true;
  try {
    return Boolean(window.sessionStorage.getItem(STORE_KEY));
  } catch (error) {
    return false;
  }
}

function readState() {
  try {
    const raw = window.sessionStorage.getItem(STORE_KEY);
    if (raw) return JSON.parse(raw).state || memoryState;
  } catch (error) {
    /* storage unavailable: the in-memory flag stands in */
  }
  return memoryState;
}

/** @param {'shown' | 'added' | 'dismissed'} state */
function writeState(state) {
  memoryState = state;
  try {
    window.sessionStorage.setItem(STORE_KEY, JSON.stringify({ v: 1, state, at: Date.now() }));
  } catch (error) {
    /* storage unavailable: the in-memory flag stands in */
  }
}

/**
 * @param {string} name
 * @param {Record<string, any>} detail
 */
function emit(name, detail) {
  document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
}

/**
 * @param {string} name
 * @param {Record<string, any>} payload
 */
function publish(name, payload) {
  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (error) {
    /* analytics is optional */
  }
}
